import { Loader } from "@googlemaps/js-api-loader"
import { MarkerClusterer } from "@googlemaps/markerclusterer"
import { styles, getMarkerIcon, getMarkerLabel, getGeoIcon, getHomeIcon, getCourierIcon } from '@/helpers/googleMaps'
import { mapGetters } from 'vuex'

let Map;
let MapTypeControlStyle;
let Animation;
let LatLngBounds;
let Marker;
let Point;
let Size;

export const MixinGoogleMaps = {
  props: {

  },
  data() {
    return {
      googleMapsApiKey: this.$root.options.googleMapsApiKey ?? this.themeValue('THEME_GOOGLE_MAPS_API_KEY'),
      geolocationAvailable: 'geolocation' in navigator,
			geolocationSort: false,
      activeLocation: null,
      mapElementClass: '',
      maxGeolocationDistance: 21,
			mapElementAttributes: {},
			useClusterer: true,

			mapInstance: null,
			markerInstances: null,
			markerClustererInstance: null,
			geoMarkerInstance: null,
    }
  },
  computed: {
    ...mapGetters([
			'geolocation'
		]),
    locationsEnhanced() {
      return (this.locations ?? [])
        .map(location => ({
          ...location,
          lat: location.lat ?? location.latitude,
          lng: location.lng ?? location.long ?? location.longitude,
        }))
        .filter(location => location.lat && location.lng)
    },
    locationsEnhancedGeolocation() {
      if (this.geolocation && this.geolocationSort) {
				return this.locationsEnhanced
					.map(location => {
						const distance = this.calculateDistance(location, this.geolocation)
						return {
							...location,
							distance,
						}
					})
					.sort((a, b) => {
						return a.distance - b.distance
					})
					.filter(location => location.distance < this.maxGeolocationDistance)
			}
			return []
    }
  },
  methods: {
    async setupGoogleMaps() {
      if ([LatLngBounds, Point, Size, Map, MapTypeControlStyle, Marker, Animation].some(i => i == null)) {
        const loader = new Loader({
          apiKey: this.googleMapsApiKey,
          version: "weekly",
        })

        ;({ LatLngBounds, Point, Size } = await loader.importLibrary('core'))
        ;({ Map, MapTypeControlStyle } = await loader.importLibrary('maps'))
        ;({ Marker, Animation } = await loader.importLibrary('marker'))
      }
      const mapDiv = document.createElement('div')
      mapDiv.className = this.mapElementClass
      this.$refs.mapContainer.appendChild(mapDiv)

      this.mapInstance = new Map(mapDiv, {
        center: {
          lat: 46.8182,
          lng: 8.2275
        },
        zoom: 8,
        mapTypeId: 'roadmap',
        gestureHandling: 'cooperative',
        disableDefaultUI: true,
        zoomControl: true,
        fullscreenControl: false,
        mapTypeControl: true,
        mapTypeControlOptions: {
          style: MapTypeControlStyle.DROPDOWN_MENU,
          mapTypeIds: ['roadmap', 'satellite'],
        },
				...this.mapElementAttributes,
        styles,
      })

      if (this.useClusterer) {
        this.markerClustererInstance = new MarkerClusterer({
          map: this.mapInstance,
          renderer: {
            render: ({ count, position }, stats) => {
              // create marker using svg icon
              return new Marker({
                position,
                draggable: false,
                optimized: false,
                icon: getMarkerIcon(this.themeName, { Size, Point }),
                label: {
                  text: String(count),
                  ...getMarkerLabel(this.themeName)
                },
                // title: `Cluster of ${count} markers`,
                // adjust zIndex to be above other markers
                zIndex: Number(Marker.MAX_ZINDEX) + count,
              })
            }
          }
        })
      }

      this.geoMarkerInstance = new Marker({
        draggable: false,
        // animation: Animation.DROP,
        optimized: false,
        icon: getGeoIcon(this.themeName, { Size, Point }),
        zIndex: 50,
      })

      this.geoMarkerInstance.addListener('click', () => {
        this.mapInstance.panTo(this.geoMarkerInstance.position)
      })

			setTimeout(() => {
				this.setupMarkers()
			}, 100)
		},
		setupMarkers() {
			this.clearMarkers()
			this.createMarkers()
			this.addVisibleMarkers()
			this.mapInstance.setZoom(8)

			setTimeout(() => {
				this.mapInstance.setZoom(8)
				setTimeout(() => {
					this.panToAll()
				}, 100)
			}, 100)
		},
		clearMarkers() {
      if (this.useClusterer) {
				this.markerClustererInstance?.clearMarkers()
			} else {
				this.markerInstances?.forEach(marker => marker.setMap(null))
			}
      this.markerInstances = null
		},
		createMarkers() {
			this.markerInstances = this.locationsEnhanced
        .filter(location => location.lat && location.lng)
        .map(location => {
          let markerFunction = getMarkerIcon

          if (location.brand === 'home') {
            markerFunction = getHomeIcon
          }

          if (location.brand === 'courier') {
            markerFunction = getCourierIcon
          }

          const marker = new Marker({
            // map,
            position: { lat: +location.lat, lng: +location.lng },
            title: location.title,
            draggable: false,
            animation: Animation.DROP,
            optimized: false,
            icon: markerFunction(this.themeName, { Size, Point }),
            zIndex: 100,
            id: location.id,
            visible: true,
            ignore: false,
						...location.markerAttributes
          })

          marker.addListener('click', () => {
            this.selectLocation(location)
          })

          return marker
        })
		},
    removeMarker(id) {
			const markerIndex = this.markerInstances.findIndex(marker => marker.id === id)
			const marker = this.markerInstances[markerIndex]

			if (marker) {
				marker.setMap(null)
				this.markerInstances.splice(markerIndex, 1)
				this.addVisibleMarkers()
				this.panToAll()
			}
		},
		removeMarkers(ids = []) {
			ids?.forEach(id => {
				const markerIndex = this.markerInstances?.findIndex(marker => marker.id === id)
				const marker = this.markerInstances?.[markerIndex]

				if (marker) {
					marker.setMap(null)
					this.markerInstances?.splice(markerIndex, 1)
				}
			})

			this.addVisibleMarkers()
			this.panToAll()
		},
		updateMarkerPosition(id, lat, lng) {
			const marker = this.markerInstances?.find(marker => marker.id === id)

			if (marker) {
				marker.setPosition({ lat, lng })
			}
		},
		addVisibleMarkers() {
      if (this.useClusterer) {
				this.markerClustererInstance?.clearMarkers()
				this.markerClustererInstance?.addMarkers(this.markerInstances.filter(marker => marker.visible !== false))
			} else {
				this.markerInstances.forEach(marker => marker.setMap(marker.visible ? this.mapInstance : null))
			}
		},
		panToAll() {
			if (this.markerInstances?.length > 1) {
				const bounds = new LatLngBounds()
				this.markerInstances.forEach(marker => marker.visible && !marker.ignore && bounds.extend(marker.getPosition()))

				if (this.geolocation) {
					bounds.extend({ lat: this.geolocation.latitude, lng: this.geolocation.longitude })
				}

				this.mapInstance.fitBounds(bounds)
			}
			else if (this.markerInstances?.length) {
				this.panToMarker(this.markerInstances[0].id)
			}
		},
		panToMarker(id) {
			const marker = this.markerInstances.find(marker => marker.id === id)

			if (marker) {
				const bounds = new LatLngBounds()
				bounds.extend(marker.getPosition())
				this.mapInstance.fitBounds(bounds)
        setTimeout(() => {
          this.mapInstance.setZoom(this.mapInstance.getZoom() - 5)
          this.mapInstance.panTo(marker.position)
        }, 100)
			}
		},
    geolocationRequest(sort) {
			this.loading = true

      if (sort) {
        this.geolocationSort = true
      }
			navigator.geolocation.getCurrentPosition(this.geolocationSuccess.bind(this), this.geolocationError.bind(this), {
				enableHighAccuracy: true,
				// timeout: 5000,
				// maximumAge: 0
			})
		},
		geolocationSuccess(geolocation) {
      this.$store.dispatch('setGeolocation', geolocation.coords)
			this.loading = false
			const activeIds = this.locationsEnhancedGeolocation.map(location => location.id)
			this.markerInstances.forEach(marker => marker.visible = activeIds.includes(marker.id))
			this.geoMarkerInstance.setPosition({ lat: this.geolocation.latitude, lng: this.geolocation.longitude })
			this.geoMarkerInstance.setMap(this.mapInstance)
			this.addVisibleMarkers()
			this.panToAll()
		},
		geolocationError() {
      this.geolocationSort = false
			this.geolocationAvailable = false
			this.loading = false
		},
		geolocationReset() {
      this.geolocationSort = false
			this.markerInstances.forEach(marker => marker.visible = true)
			this.geoMarkerInstance.setMap(null)
			this.addVisibleMarkers()
			this.panToAll()
		},
    async geolocationCheck() {
			try {
        const result = await navigator.permissions.query({ name: 'geolocation' })
        if (result.state === 'granted') {
			    this.geolocationAvailable = true
        } else if (result.state === 'denied') {
          this.geolocationSort = false
			    this.geolocationAvailable = false
        }

        result.addEventListener('change', () => {
          this.geolocationCheck()
        }, { once: true });
			}
			catch(err) {
				// nothing to do
			}
		},
    geoLocationUpdate(coords) {
			this.geoMarkerInstance.setPosition(coords)
		},
    async selectLocation(location) {
      if (this.activeLocation !== location.id) {
        this.activeLocation = location.id
        this.panToMarker(location.id)
        this.$emit('map:location', location.id)
      } else {
        this.panToAll()
      }
		},
		calculateDistance(location, { latitude, longitude } = {}) {
			const radlat1 = Math.PI * latitude / 180
			const radlat2 = Math.PI * location.lat / 180
			const theta = longitude - location.lng
			const radtheta = Math.PI * theta / 180
			let distance = Math.sin(radlat1) * Math.sin(radlat2) + Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta)
			distance = Math.acos(distance)
			distance = distance * 180 / Math.PI
			distance = distance * 60 * 1.1515
			distance = distance * 1.609344
			return distance
		},
  },
  async created() {
  },
  mounted() {
  },
  destroyed() {
    try {
      this.clearMarkers()
    } catch(err) {
      this.isDebug() && console.error(err)
    }
  }
};

export default MixinGoogleMaps;
